import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardRadio } from '@/components/ui/CardRadio'
import { DateInput } from '@/components/ui/DateInput'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { formatDate } from '@/lib/formatters'
import {
  addLossRunClaim,
  fillApplicationErrorMessage,
  invalidateFillApplication,
  useFillApi,
} from '@/models/fillApplication'
import type { LossRunRequest } from '@/models/lossRunRequest'
import { ClaimFields } from '../ClaimFields'
import { claimFormError, type ClaimFormState, claimFormToPayload, EMPTY_CLAIM_FORM } from '../claimForm'

const NEW_LOSS_RUN = 'new'

interface AddClaimDialogProps {
  orgId: string
  lossRuns: LossRunRequest[]
  onOpenChange: (open: boolean) => void
}

/** Mounted only while open, so every opening starts blank. */
export function AddClaimDialog({ orgId, lossRuns, onOpenChange }: AddClaimDialogProps) {
  const api = useFillApi()
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<string>(lossRuns[0]?.id ?? NEW_LOSS_RUN)
  const [insurer, setInsurer] = useState('')
  const [start, setStart] = useState<Date | undefined>()
  const [end, setEnd] = useState<Date | undefined>()
  const [claim, setClaim] = useState<ClaimFormState>(EMPTY_CLAIM_FORM)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const isNew = target === NEW_LOSS_RUN

  async function save() {
    const problem =
      isNew && (!insurer.trim() || !start || !end)
        ? 'Add the insurer and the policy period for the new loss run.'
        : claimFormError(claim)
    if (problem) {
      setError(problem)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await addLossRunClaim(
        orgId,
        {
          ...(isNew
            ? {
                newLossRun: {
                  insurer: insurer.trim(),
                  dateStart: format(start!, 'yyyy-MM-dd'),
                  dateEnd: format(end!, 'yyyy-MM-dd'),
                },
              }
            : { lossRunRequestId: target }),
          ...claimFormToPayload(claim),
        },
        api
      )
      toast.success('Claim added')
      onOpenChange(false)
      await invalidateFillApplication(queryClient, orgId)
    } catch (saveError) {
      setError(fillApplicationErrorMessage(saveError))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add loss run history</DialogTitle>
          <DialogDescription>This adds one claim. The loss runs Cai read stay as they are.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Add it to</span>
          {lossRuns.map((run) => (
            <CardRadio key={run.id} isSelected={target === run.id} onSelect={() => setTarget(run.id)}>
              {run.insuranceProviderContact.insuranceName} · {formatDate(run.dateStart)} –{' '}
              {formatDate(run.dateEnd)}
            </CardRadio>
          ))}
          <CardRadio isSelected={isNew} onSelect={() => setTarget(NEW_LOSS_RUN)}>
            A new loss run
          </CardRadio>
        </div>
        {isNew && (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="add-claim-insurer">Insurer</FieldLabel>
              <Input
                id="add-claim-insurer"
                value={insurer}
                onChange={(event) => setInsurer(event.target.value)}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="add-claim-start">Policy start</FieldLabel>
                <DateInput value={start} onChange={setStart} inputProps={{ id: 'add-claim-start' }} />
              </Field>
              <Field>
                <FieldLabel htmlFor="add-claim-end">Policy end</FieldLabel>
                <DateInput value={end} onChange={setEnd} inputProps={{ id: 'add-claim-end' }} />
              </Field>
            </div>
          </FieldGroup>
        )}
        <ClaimFields value={claim} onChange={setClaim} disabled={saving} idPrefix="add-claim" />
        <FieldError>{error}</FieldError>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void save()} loading={saving ? 'Adding…' : undefined}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
